import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useContact } from '../context/ContactContext';
import { getAllAirlines } from '../data/airlines';
import './AirlineSupport.css';

const AirlineSupport = () => {
  const { contactSettings } = useContact();
  const tfn = contactSettings?.tfn || '+1-888-859-0441';
  const tfnClean = tfn.replace(/[^0-9+]/g, '');
  const allAirlines = getAllAirlines();
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSticky, setShowSticky] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const filtered = searchQuery
    ? allAirlines.filter(a => a.name.toLowerCase().includes(searchQuery.toLowerCase()) || a.code.toLowerCase().includes(searchQuery.toLowerCase()))
    : allAirlines;

  const services = [
    { icon: '✈️', title: 'Flight Booking Assistance', desc: 'Our travel agents help you compare fares and book flights across multiple airlines at competitive prices.', img: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80' },
    { icon: '🔄', title: 'Itinerary Changes', desc: 'Need to change your travel dates or route? We guide you through the modification process with your airline.', img: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=400&q=80' },
    { icon: '💰', title: 'Cancellation Guidance', desc: 'We help you understand cancellation policies, fees, and walk you through the refund request process.', img: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&q=80' },
    { icon: '🧳', title: 'Travel Planning', desc: 'From multi-city trips to vacation packages — our agents help plan your entire travel itinerary.', img: 'https://images.unsplash.com/photo-1565026057447-bc90a3dceb87?w=400&q=80' },
    { icon: '💺', title: 'Fare Comparison', desc: 'We search across airlines to find you the best available fares for your preferred route and dates.', img: 'https://images.unsplash.com/photo-1540339832862-474599807836?w=400&q=80' },
    { icon: '🌍', title: 'International Trips', desc: 'Planning an international trip? We assist with multi-city bookings, stopovers, and route planning.', img: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=400&q=80' },
  ];

  const faqs = [
    { q: 'What exactly is Fly Travel Deals?', a: 'Fly Travel Deals is an independent travel agency and booking assistance service. We are NOT an airline. We help travelers find, compare, and book flights across multiple airlines. Think of us as your personal travel agent available by phone.' },
    { q: 'Are you the official customer service for any airline?', a: 'No, absolutely not. We are a third-party independent travel agency. If you need official airline support for an existing booking made directly with an airline, you should contact that airline directly through their official website or app.' },
    { q: 'What can your travel agents help me with?', a: `Our agents at ${tfn} can help you search for flights, compare fares across airlines, book new reservations, guide you through change/cancellation policies, and plan multi-city or international itineraries.` },
    { q: 'Do you charge a service fee?', a: 'As an independent travel agency, we may charge a service fee for bookings made through our agents. All fees are clearly disclosed before you confirm any reservation. There are no hidden charges.' },
    { q: 'Is your service available 24/7?', a: `Yes! Our travel assistance line at ${tfn} is available 24 hours a day, 7 days a week, including holidays.` },
    { q: 'Which airlines can you book flights with?', a: 'We can search fares and assist with bookings across all major domestic and international airlines including American, Delta, United, Southwest, JetBlue, Emirates, British Airways, Lufthansa, and many more.' },
    { q: 'Can I trust booking through your service?', a: 'Yes. We are a legitimate travel agency. Your payment information is processed securely, and you will receive a proper booking confirmation for any reservation made through us.' },
    { q: 'What if I already have a booking directly with an airline?', a: 'If you booked directly with an airline, we recommend contacting that airline directly for changes or issues. Our agents can provide general guidance, but the airline\'s own support team will have direct access to your reservation.' },
  ];

  const reviews = [
    { name: 'Sarah M.', loc: 'New York', text: 'Found me an amazing fare I couldn\'t find online. The agent was transparent about their service fee and it was totally worth it.', stars: 5, img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80' },
    { name: 'James W.', loc: 'Los Angeles', text: 'Great travel agency! They compared prices across multiple airlines and saved me over $300 on my family trip.', stars: 5, img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80' },
    { name: 'Maria G.', loc: 'Miami', text: 'Needed help planning a complex multi-city international trip. The agent was knowledgeable and patient. Excellent service!', stars: 5, img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80' },
    { name: 'Robert C.', loc: 'Chicago', text: 'Used their service to rebook when my plans changed. They explained everything clearly and found me a better deal.', stars: 5, img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80' },
  ];

  return (
    <>
      <Helmet>
        <title>Book Cheap Flights by Phone | 24/7 Travel Agency | Fly Travel Deals | {tfn}</title>
        <meta name="description" content={`Book cheap flights by phone — call ${tfn}. Fly Travel Deals is an independent travel agency offering 24/7 flight booking, fare comparison & travel planning across all major airlines. No hidden fees.`} />
        <meta name="keywords" content="spirit airline, phone number for booking, alaska airlines flights, domestic flight booking assistance, united airlines flights, ana airlines, flight change assistance, air canada flights, economy booking phone number, airlines, airlines phone, booking contact airlines, klm flights, british airways flights, last minute flight help, help with travel booking, travel booking support, change my flight, flight reservation help, reservation support travel, jetblue flights, airline tickets, delta airline, virgin flights, tap portugal, tap air portugal, book flights with assistance, same day flight change help, modify flight booking help, klm airlines, airline reservation, urgent flight change help, international flight booking help, flight rescheduling assistance, priceline flights, change flight help, travel change assistance" />
        <link rel="canonical" href="https://flytraveldeals.us/travel-assistance" />
        <meta property="og:title" content={`Travel Assistance & Booking Help | Fly Travel Deals`} />
        <meta property="og:description" content="Independent travel agency offering 24/7 flight booking assistance, fare comparison, and trip planning across all major airlines." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"TravelAgency","name":"Fly Travel Deals","url":"https://flytraveldeals.us","description":"Independent travel agency providing flight booking assistance, fare comparison, and travel planning services.","contactPoint":[{"@type":"ContactPoint","telephone":tfn,"contactType":"reservations","areaServed":"US","availableLanguage":["English","Spanish"]}]})}</script>
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":faqs.map(f=>({"@type":"Question","name":f.q,"acceptedAnswer":{"@type":"Answer","text":f.a}}))})}</script>
      </Helmet>

      <div className="as-page">

        {/* STICKY BAR */}
        <div className={`as-sticky ${showSticky ? 'as-sticky-show' : ''}`}>
          <div className="as-sticky-inner">
            <span>✈️ Independent Travel Agency — Book by Phone</span>
            <a href={`tel:${tfnClean}`} className="as-sticky-btn">📞 {tfn}</a>
          </div>
        </div>

        {/* ===== HERO ===== */}
        <section className="as-hero">
          <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80" alt="Airplane flying through clouds" className="as-hero-img" />
          <div className="as-hero-overlay" />
          <div className="as-hero-content">
            <div className="as-hero-badge">
              <span className="as-live-dot" />
              <span>Independent Travel Agency — Not an Airline</span>
            </div>
            <h1>Travel Assistance &<br />Flight Booking Help</h1>
            <p className="as-hero-desc">
              We're an <strong>independent travel agency</strong> that helps you find, compare, and book flights
              across all major airlines. Talk to a real travel agent who'll find you the best deals.
            </p>
            <div className="as-hero-actions">
              <a href={`tel:${tfnClean}`} className="as-hero-call">
                <div className="as-call-ripple" />
                <span className="as-call-icon">📞</span>
                <div>
                  <small>Speak to a Travel Agent</small>
                  <strong>{tfn}</strong>
                </div>
              </a>
              <a href="#airlines" className="as-hero-explore">View Airlines We Book ↓</a>
            </div>
            <div className="as-hero-stats">
              <div><strong>50K+</strong><span>Bookings Made</span></div>
              <div><strong>4.9/5</strong><span>Customer Rating</span></div>
              <div><strong>200+</strong><span>Airlines Covered</span></div>
              <div><strong>24/7</strong><span>Availability</span></div>
            </div>
          </div>
        </section>

        {/* ===== TRANSPARENCY BANNER ===== */}
        <section className="as-transparency">
          <div className="as-container">
            <div className="as-transparency-inner">
              <div className="as-transparency-icon">ℹ️</div>
              <div className="as-transparency-text">
                <strong>We are Fly Travel Deals — an independent travel agency.</strong>
                <span> We are not affiliated with, endorsed by, or connected to any airline. We are a third-party booking service that helps travelers find and compare flights. Service fees may apply.</span>
              </div>
            </div>
          </div>
        </section>

        {/* ===== WHY US - IMAGE SPLIT ===== */}
        <section className="as-why">
          <div className="as-container">
            <div className="as-why-grid">
              <div className="as-why-images">
                <img src="https://images.unsplash.com/photo-1521727284390-39ca72a09ac2?w=600&q=80" alt="Travel agent helping customer" className="as-why-img-main" />
                <img src="https://images.unsplash.com/photo-1529074963764-98f45c47344b?w=400&q=80" alt="Airplanes at terminal" className="as-why-img-float" />
                <div className="as-why-stat-card">
                  <span className="as-stat-big">50K+</span>
                  <span>Trips Booked</span>
                </div>
              </div>
              <div className="as-why-text">
                <span className="as-tag">Why Book With Us</span>
                <h2>Your Personal Travel<br />Agent, Just a Call Away</h2>
                <p>Skip the endless searching across multiple websites. Our experienced travel agents compare fares, find hidden deals, and handle the entire booking process for you — by phone.</p>
                <div className="as-why-features">
                  <div className="as-why-feat">
                    <div className="as-feat-icon">🔍</div>
                    <div><h4>Multi-Airline Fare Search</h4><p>We compare prices across 200+ airlines to find your best deal.</p></div>
                  </div>
                  <div className="as-why-feat">
                    <div className="as-feat-icon">👤</div>
                    <div><h4>Real Human Agents</h4><p>No bots or automated menus — speak to a trained travel professional.</p></div>
                  </div>
                  <div className="as-why-feat">
                    <div className="as-feat-icon">🛡️</div>
                    <div><h4>Transparent Pricing</h4><p>All fees clearly disclosed upfront before you confirm any booking.</p></div>
                  </div>
                  <div className="as-why-feat">
                    <div className="as-feat-icon">🌐</div>
                    <div><h4>Domestic & International</h4><p>From local flights to complex multi-city international itineraries.</p></div>
                  </div>
                </div>
                <a href={`tel:${tfnClean}`} className="as-btn-green">📞 Speak to a Travel Agent</a>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SERVICES WITH IMAGES ===== */}
        <section className="as-services">
          <div className="as-container">
            <div className="as-section-head">
              <span className="as-tag">Our Services</span>
              <h2>How Our Travel Agents Help You</h2>
              <p>From finding the best fare to planning your entire trip — here's what we do.</p>
            </div>
            <div className="as-services-grid">
              {services.map((s, i) => (
                <div key={i} className="as-svc-card">
                  <div className="as-svc-img-wrap">
                    <img src={s.img} alt={s.title} loading="lazy" />
                    <div className="as-svc-img-overlay" />
                    <span className="as-svc-emoji">{s.icon}</span>
                  </div>
                  <div className="as-svc-body">
                    <h3>{s.title}</h3>
                    <p>{s.desc}</p>
                    <a href={`tel:${tfnClean}`} className="as-svc-cta">📞 Talk to an Agent</a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FULL-WIDTH IMAGE CTA ===== */}
        <section className="as-img-cta">
          <img src="https://images.unsplash.com/photo-1556388158-158ea5ccacbd?w=1920&q=80" alt="Traveler at airport with luggage" className="as-img-cta-bg" />
          <div className="as-img-cta-overlay" />
          <div className="as-img-cta-content">
            <h2>Let a Travel Expert<br />Find Your Best Fare</h2>
            <p>Our agents search across multiple airlines to find deals you might miss online.</p>
            <a href={`tel:${tfnClean}`} className="as-img-cta-btn">
              <span className="as-phone-bounce">📞</span>
              <div>
                <small>Independent Travel Agency • 24/7</small>
                <strong>{tfn}</strong>
              </div>
            </a>
          </div>
        </section>

        {/* ===== AIRLINES ===== */}
        <section className="as-airlines" id="airlines">
          <div className="as-container">
            <div className="as-section-head">
              <span className="as-tag">Airlines We Book</span>
              <h2>We Search Fares Across 200+ Airlines</h2>
              <p>As an independent agency, we compare prices across all these carriers and more.</p>
            </div>
            <div className="as-airline-search-wrap">
              <span className="as-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search airlines (e.g., Delta, Emirates, AA)..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="as-airline-search"
              />
            </div>
            <div className="as-airlines-grid">
              {filtered.map((a, i) => (
                <div key={i} className="as-air-card">
                  <div className="as-air-card-header">
                    <img src={`https://images.kiwi.com/airlines/64/${a.code}.png`} alt={a.name} className="as-air-logo" loading="lazy" />
                    <div>
                      <h3>{a.name}</h3>
                      <div className="as-air-badges">
                        <span className="as-air-code">{a.code}</span>
                        {a.alliance && !a.alliance.includes('None') && <span className="as-air-alliance">{a.alliance}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="as-air-stats">
                    <div><span>{a.destinations}+</span><small>Routes</small></div>
                    <div><span>{a.fleet}</span><small>Aircraft</small></div>
                    <div><span>{a.hubs?.length || '—'}</span><small>Hubs</small></div>
                  </div>
                  <a href={`tel:${tfnClean}`} className="as-air-cta">
                    <span>📞 Book {a.name} Flights</span>
                  </a>
                </div>
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="as-no-results">
                <p>No airlines found matching your search. We can still book flights with <strong>any airline</strong>!</p>
                <a href={`tel:${tfnClean}`} className="as-btn-green">📞 Call {tfn}</a>
              </div>
            )}
            <div className="as-airline-note">
              <p>⚠️ <strong>Note:</strong> We are NOT the official customer service for any of these airlines. We are an independent travel agency that books flights on these carriers. Airline logos shown for identification only.</p>
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="as-how">
          <div className="as-container">
            <div className="as-section-head as-head-light">
              <span className="as-tag as-tag-light">How It Works</span>
              <h2>Book Your Flight in 3 Easy Steps</h2>
            </div>
            <div className="as-how-steps">
              <div className="as-how-step">
                <div className="as-how-num">01</div>
                <img src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=400&q=80" alt="Phone call" className="as-how-img" />
                <h3>Call Our Agency</h3>
                <p>Dial <strong>{tfn}</strong> and tell our travel agent where and when you'd like to fly.</p>
              </div>
              <div className="as-how-connector" />
              <div className="as-how-step">
                <div className="as-how-num">02</div>
                <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&q=80" alt="Comparing fares" className="as-how-img" />
                <h3>We Compare Fares</h3>
                <p>Our agent searches across multiple airlines to find you the best available price.</p>
              </div>
              <div className="as-how-connector" />
              <div className="as-how-step">
                <div className="as-how-num">03</div>
                <img src="https://images.unsplash.com/photo-1522199755839-a2bacb67c546?w=400&q=80" alt="Booking confirmed" className="as-how-img" />
                <h3>Confirm & Fly</h3>
                <p>Review the fare, confirm your booking, and receive your itinerary by email.</p>
              </div>
            </div>
            <div className="as-how-cta">
              <a href={`tel:${tfnClean}`} className="as-btn-white">📞 Call {tfn} — Book Now</a>
            </div>
          </div>
        </section>

        {/* ===== REVIEWS ===== */}
        <section className="as-reviews">
          <div className="as-container">
            <div className="as-section-head">
              <span className="as-tag">Reviews</span>
              <h2>What Our Customers Say</h2>
            </div>
            <div className="as-reviews-grid">
              {reviews.map((r, i) => (
                <div key={i} className="as-review-card">
                  <div className="as-review-stars">{'★'.repeat(r.stars)}</div>
                  <p>"{r.text}"</p>
                  <div className="as-review-author">
                    <img src={r.img} alt={r.name} />
                    <div>
                      <strong>{r.name}</strong>
                      <span>{r.loc}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== IMAGE CTA 2 ===== */}
        <section className="as-split-cta">
          <div className="as-split-img">
            <img src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800&q=80" alt="Travelers at airport" />
          </div>
          <div className="as-split-content">
            <span className="as-tag">Why Use a Travel Agent?</span>
            <h2>Save Time.<br />Save Money.<br />Travel Smarter.</h2>
            <ul className="as-check-list">
              <li>We search fares across 200+ airlines at once</li>
              <li>Experienced agents find deals you may miss online</li>
              <li>One call handles your entire booking process</li>
              <li>Clear pricing with no hidden fees</li>
              <li>24/7 availability including holidays</li>
            </ul>
            <a href={`tel:${tfnClean}`} className="as-split-call">
              <span className="as-phone-bounce">📞</span>
              <div>
                <small>Talk to a Travel Agent</small>
                <strong>{tfn}</strong>
              </div>
            </a>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="as-faq" id="faq">
          <div className="as-container">
            <div className="as-faq-layout">
              <div className="as-faq-left">
                <span className="as-tag">FAQ</span>
                <h2>Frequently Asked<br />Questions</h2>
                <p>Have a question? Find answers below or talk to an agent.</p>
                <a href={`tel:${tfnClean}`} className="as-btn-green">📞 Call {tfn}</a>
                <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&q=80" alt="Travel agent support" className="as-faq-img" />
              </div>
              <div className="as-faq-right">
                {faqs.map((f, i) => (
                  <div key={i} className={`as-faq-item ${openFaq === i ? 'open' : ''}`}>
                    <button className="as-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                      <span>{f.q}</span>
                      <span className="as-faq-icon">{openFaq === i ? '−' : '+'}</span>
                    </button>
                    {openFaq === i && (
                      <div className="as-faq-a">
                        <p>{f.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== SEO TAGS — Google Ads keyword aligned ===== */}
        <section className="as-seo-tags">
          <div className="as-container">
            <h3>Popular Flight Searches</h3>
            <div className="as-tags-wrap">
              {[
                'spirit airline','phone number for booking','alaska airlines flights',
                'domestic flight booking assistance','united airlines flights','ana airlines',
                'flight change assistance','air canada flights','economy booking phone number',
                'airlines','airlines phone','booking contact airlines','klm flights',
                'british airways flights','last minute flight help','help with travel booking',
                'travel booking support','change my flight','flight reservation help',
                'reservation support travel','jetblue flights','airline tickets',
                'delta airline','virgin flights','tap portugal','tap air portugal',
                'book flights with assistance','same day flight change help',
                'modify flight booking help','klm airlines','airline reservation',
                'urgent flight change help','international flight booking help',
                'flight rescheduling assistance','priceline flights',
                'change flight help','travel change assistance',
              ].map((t,i) => (
                <span key={i} className="as-seo-tag">{t}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ===== DISCLAIMER ===== */}
        <section className="as-disc">
          <div className="as-container">
            <div className="as-disc-box">
              <strong>⚠️ Important Disclosure:</strong> Fly Travel Deals is an <strong>independent travel agency</strong>. We are NOT an airline, and we are NOT the official customer service, reservations line, or help desk of any airline. We are a separate, third-party travel booking service. We help customers search for, compare, and book flights across multiple airlines. A service fee may apply to bookings made through our agents. All airline names, logos, and trademarks displayed on this page belong to their respective owners and are used solely for identification purposes. For official airline customer service, please contact the airline directly through their website.
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="as-final">
          <img src="https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=1920&q=80" alt="Sunset over airplane" className="as-final-bg" />
          <div className="as-final-overlay" />
          <div className="as-final-content">
            <h2>Ready to Book Your Trip?</h2>
            <p>Talk to a travel agent who'll find you the best fare — across any airline.</p>
            <a href={`tel:${tfnClean}`} className="as-final-btn">
              <div className="as-final-pulse" />
              <span className="as-phone-bounce">📞</span>
              <div>
                <small>Independent Travel Agency • 24/7</small>
                <strong>{tfn}</strong>
              </div>
            </a>
            <div className="as-final-chips">
              <span>✓ Not an Airline</span>
              <span>✓ Independent Agency</span>
              <span>✓ 200+ Airlines</span>
              <span>✓ Best Fares</span>
              <span>✓ 24/7</span>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default AirlineSupport;
